if (!customElements.get("alloy-kit-request-form")) {
  const ENDPOINT_PATH = "/api/v1/marketplace/kit-request";
  const STOREFRONT_ORIGIN = "https://shop.thealloymarket.com";
  const ANALYTICS_KEYS = {
    utm_source: 128,
    utm_medium: 128,
    utm_campaign: 128,
    utm_term: 128,
    utm_content: 128,
    irclickid: 256,
    gclid: 256,
    msclkid: 256,
    fbclid: 256,
  };

  customElements.define(
    "alloy-kit-request-form",
    class AlloyKitRequestForm extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === "true") return;
        this.dataset.initialized = "true";
        this.form = this.querySelector("form");
        this.submitButton = this.querySelector("[data-submit-button]");
        this.submitLabel = this.querySelector("[data-submit-label]");
        this.errorSummary = this.querySelector("[data-error-summary]");
        this.errorList = this.querySelector("[data-error-list]");
        this.success = this.querySelector("[data-success]");
        this.failure = this.querySelector("[data-failure]");
        this.failureMessage = this.querySelector("[data-failure-message]");
        this.referralToggle = this.form?.elements.namedItem("hasReferral");
        this.referralField = this.querySelector("[data-referral-field]");
        this.inFlight = false;
        this.pendingIdempotencyKey = "";
        this.pendingPayload = "";

        if (!this.form) return;
        this.referralToggle?.addEventListener("change", () => this.syncReferralField());
        this.form.addEventListener("submit", (event) => this.handleSubmit(event));
        this.syncReferralField();
      }

      syncReferralField() {
        if (!this.referralToggle || !this.referralField) return;
        const expanded = this.referralToggle.checked;
        this.referralField.hidden = !expanded;
        this.referralToggle.setAttribute("aria-expanded", String(expanded));
        if (!expanded) {
          const referralInput = this.form.elements.namedItem("referralCode");
          if (referralInput) referralInput.value = "";
        }
      }

      clearErrors() {
        this.errorSummary.hidden = true;
        this.errorList.replaceChildren();
        this.form.querySelectorAll("[data-error-for]").forEach((message) => {
          message.hidden = true;
          message.textContent = "";
        });
        this.form.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
          field.removeAttribute("aria-invalid");
          field.removeAttribute("aria-describedby");
        });
      }

      values() {
        return Object.fromEntries(new FormData(this.form));
      }

      validate(values = this.values()) {
        const errors = {};
        const required = {
          firstName: "Enter your first name.",
          lastName: "Enter your last name.",
          address: "Enter a street address.",
          city: "Enter a city.",
          state: "Select a state.",
          zip: "Enter a ZIP code.",
          phone: "Enter a mobile number.",
          email: "Enter an email address.",
        };
        Object.entries(required).forEach(([name, message]) => {
          if (!String(values[name] || "").trim()) errors[name] = message;
        });

        const address = String(values.address || "").trim();
        const poBoxPattern = /\bP(?:OST(?:AL)?)?[\s.-]*O(?:FFICE)?[\s.-]*BOX\b|\bP[\s.-]*O[\s.-]*BOX\b/i;
        if (address && poBoxPattern.test(address)) {
          errors.address = "PO Box addresses are not allowed. Enter a valid street address.";
        }
        const zip = String(values.zip || "").trim();
        if (zip && !/^\d{5}(?:-\d{4})?$/.test(zip)) {
          errors.zip = "Enter a valid 5-digit or ZIP+4 code.";
        }
        const phoneDigits = String(values.phone || "").replace(/\D/g, "");
        if (phoneDigits && phoneDigits.length !== 10) {
          errors.phone = "Please enter a valid 10-digit mobile number.";
        }
        const emailField = this.form.elements.namedItem("email");
        if (values.email && emailField && !emailField.validity.valid) {
          errors.email = "Please enter a valid email address.";
        }
        return errors;
      }

      showErrors(errors) {
        Object.entries(errors).forEach(([name, message]) => {
          const field = this.form.elements.namedItem(name);
          const messageElement = this.querySelector(`[data-error-for="${name}"]`);
          if (!field || !messageElement) return;
          field.setAttribute("aria-invalid", "true");
          field.setAttribute("aria-describedby", messageElement.id);
          messageElement.textContent = message;
          messageElement.hidden = false;
          const item = document.createElement("li");
          const link = document.createElement("a");
          link.href = `#${field.id}`;
          link.textContent = message;
          item.append(link);
          this.errorList.append(item);
        });
        this.errorSummary.hidden = false;
        this.errorSummary.focus();
        this.emitOutcome("validation_error", { fieldCount: Object.keys(errors).length });
      }

      setSubmitting(isSubmitting) {
        this.inFlight = isSubmitting;
        this.submitButton.disabled = isSubmitting;
        this.submitButton.setAttribute("aria-busy", String(isSubmitting));
        this.submitLabel.textContent = isSubmitting ? "Working..." : "Submit";
      }

      emitOutcome(outcome, extra = {}) {
        this.dispatchEvent(new CustomEvent("alloy:kit-request", {
          bubbles: true,
          detail: { outcome, ...extra },
        }));
      }

      async currentConsent() {
        if (!window.Shopify?.loadFeatures) return { analytics: false, marketing: false };
        await new Promise((resolve) => {
          window.Shopify.loadFeatures(
            [{ name: "consent-tracking-api", version: "0.1" }],
            () => resolve(),
          );
        });
        const privacy = window.Shopify?.customerPrivacy;
        if (!privacy) return { analytics: false, marketing: false };
        const consent = privacy.currentVisitorConsent?.() || {};
        return {
          analytics: consent.analytics === "yes" && privacy.analyticsProcessingAllowed?.() === true,
          marketing: consent.marketing === "yes" && privacy.marketingAllowed?.() === true,
        };
      }

      cookie(name) {
        const prefix = `${encodeURIComponent(name)}=`;
        return document.cookie.split(";").map((part) => part.trim())
          .find((part) => part.startsWith(prefix))?.slice(prefix.length) || "";
      }

      bounded(value, maximum) {
        return String(value || "").trim().slice(0, maximum);
      }

      device() {
        if (!window.matchMedia) return "unknown";
        if (window.matchMedia("(max-width: 767px)").matches) return "mobile";
        if (window.matchMedia("(max-width: 1024px)").matches) return "tablet";
        return "desktop";
      }

      referrerClass(url) {
        if (!url) return "direct";
        try {
          const host = new URL(url).hostname.toLowerCase();
          if (host === window.location.hostname.toLowerCase()) return "referral";
          if (/google\.|bing\.|yahoo\.|duckduckgo\./.test(host)) return "organic_search";
          if (/facebook\.|instagram\.|tiktok\.|linkedin\.|pinterest\.|x\.com$|twitter\./.test(host)) return "organic_social";
          return "referral";
        } catch (_error) {
          return "unknown";
        }
      }

      pageContext() {
        if (window.location.origin !== STOREFRONT_ORIGIN || window.location.protocol !== "https:") return undefined;
        return {
          name: this.bounded(document.title, 160),
          uri: `${STOREFRONT_ORIGIN}${window.location.pathname}`.slice(0, 500),
        };
      }

      attribution() {
        const params = new URLSearchParams(window.location.search);
        const attribution = {};
        Object.entries(ANALYTICS_KEYS).forEach(([key, maximum]) => {
          const value = this.bounded(params.get(key), maximum);
          if (value) attribution[key] = value;
        });
        const medium = String(attribution.utm_medium || "").toLowerCase();
        if (attribution.gclid || attribution.msclkid) attribution.referrer_class = "paid_search";
        else if (attribution.fbclid) attribution.referrer_class = "paid_social";
        else if (medium === "email" || medium === "e-mail") attribution.referrer_class = "email";
        else attribution.referrer_class = this.referrerClass(document.referrer);
        return attribution;
      }

      async payload(values) {
        const consent = await this.currentConsent();
        const payload = {
          first_name: this.bounded(values.firstName, 120),
          last_name: this.bounded(values.lastName, 120),
          email: this.bounded(values.email, 254),
          phone: this.bounded(values.phone, 40),
          address: {
            line1: this.bounded(values.address, 200),
            city: this.bounded(values.city, 120),
            state_code: this.bounded(values.state, 2).toUpperCase(),
            postal_code: this.bounded(values.zip, 10),
            country_code: "US",
          },
          device: this.device(),
          consent,
          website: "",
        };
        const line2 = this.bounded(values.address2, 200);
        const referralCode = this.bounded(values.referralCode, 120);
        const page = this.pageContext();
        if (line2) payload.address.line2 = line2;
        if (referralCode) payload.referral_code = referralCode;
        if (page?.name || page?.uri) payload.page = page;
        if (consent.analytics) {
          let hubspotutk = "";
          try {
            hubspotutk = decodeURIComponent(this.cookie("hubspotutk"));
          } catch (_error) {
            hubspotutk = "";
          }
          if (/^[a-f0-9]{32}$/i.test(hubspotutk)) payload.hubspotutk = hubspotutk;
          payload.attribution = this.attribution();
        }
        return payload;
      }

      idempotencyKey() {
        if (window.crypto?.randomUUID) return `marketplace-${window.crypto.randomUUID()}`;
        const bytes = new Uint8Array(16);
        window.crypto?.getRandomValues?.(bytes);
        return `marketplace-${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
      }

      async submitToAurify(values) {
        const endpoint = new URL(this.dataset.submissionEndpoint);
        if (endpoint.origin !== "https://aurify.app" || endpoint.pathname !== ENDPOINT_PATH || endpoint.search || endpoint.hash) {
          throw new Error("invalid_endpoint");
        }
        const payload = await this.payload(values);
        const serializedPayload = JSON.stringify(payload);
        if (serializedPayload !== this.pendingPayload) {
          this.pendingPayload = serializedPayload;
          this.pendingIdempotencyKey = this.idempotencyKey();
        }

        let response;
        try {
          response = await fetch(endpoint.href, {
            method: "POST",
            credentials: "omit",
            headers: {
              "Content-Type": "application/json",
              "Idempotency-Key": this.pendingIdempotencyKey,
            },
            body: serializedPayload,
          });
        } catch (_error) {
          return { succeeded: false, retryable: true, outcome: "network_error" };
        }

        let body = {};
        try {
          body = await response.json();
        } catch (_error) {
          return { succeeded: false, retryable: response.status >= 500, outcome: "invalid_response" };
        }
        const accepted = (response.status === 201 || response.status === 200)
          && body.status === "accepted" && typeof body.submission_id === "string";
        if (accepted) {
          return { succeeded: true, duplicate: body.duplicate === true, outcome: body.duplicate ? "duplicate" : "success" };
        }
        const retryable = body.error?.retryable === true || response.status === 429 || response.status === 503;
        return { succeeded: false, retryable, outcome: retryable ? "retryable_error" : "submission_error" };
      }

      async submitMock() {
        await new Promise((resolve) => window.setTimeout(resolve, 650));
        const succeeded = this.dataset.mockResult !== "error";
        return { succeeded, retryable: !succeeded, outcome: succeeded ? "success" : "submission_error" };
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.inFlight) return;
        this.clearErrors();
        this.failure.hidden = true;
        this.success.hidden = true;
        const values = this.values();
        const errors = this.validate(values);
        if (Object.keys(errors).length) {
          this.showErrors(errors);
          return;
        }

        this.setSubmitting(true);
        let result;
        try {
          result = this.dataset.submissionMode === "aurify"
            ? await this.submitToAurify(values)
            : await this.submitMock();
        } catch (_error) {
          result = { succeeded: false, retryable: false, outcome: "configuration_error" };
        } finally {
          this.setSubmitting(false);
        }

        if (result.succeeded) {
          this.success.hidden = false;
          this.success.focus();
          this.form.reset();
          this.syncReferralField();
          this.querySelector("[data-form-fields]").hidden = true;
          this.pendingIdempotencyKey = "";
          this.pendingPayload = "";
        } else {
          this.failureMessage.textContent = result.retryable
            ? "No information was saved. Please try again."
            : "We couldn’t process this request. Please review your information and try again.";
          this.failure.hidden = false;
          this.failure.focus();
          if (!result.retryable) {
            this.pendingIdempotencyKey = "";
            this.pendingPayload = "";
          }
        }
        this.emitOutcome(result.outcome, result.duplicate ? { duplicate: true } : {});
      }
    },
  );
}
